// ============================================================
// COVER SCREENS - the take-cover cue drawn on a figure (2026-10-25).
//
// Each threat's reading stands on the base ring as a curved screen toward that gun: 60 degrees of arc, as tall
// as the share of him the cover hides (COVER_FRACTIONS × his height), out of sight at full height, hollow. Open
// stays a flat hot arc on the ground - nothing stands between him and the gun. The coloured arc is kept on the
// ground under every screen, so the bearing reads even when the camera sees a screen edge-on (the user's pick).
//
// The figure must stand INSIDE his screens: the run of a screen behind him is drawn before his body and the
// run in front after it. Which is which is a question of the camera, so the split is done on projected points
// by their depth against the figure's own (`splitByDepth`).
//
// Pure. Zero React dependency. Ground points out, for the renderer to project (the facing ring's pattern).
// ============================================================
import type { Vec3 } from '../battlefield/types';
import { project, SCENE_VIEW, type Camera } from '../battlefield/projection';
import { tokenFoot, tokenHeight, type Token } from '../battlefield/tokens';
import { COVER_FRACTIONS } from '../battlefield/coverModel';
import type { CueLevel, ThreatReading } from '../cover/coverCue';

/** How wide a screen is, in degrees (the user ruled 60 with the ground arc kept, 2026-10-25). */
export const SCREEN_SPAN_DEG = 60;
/** How far outside the base ring the screens stand, in metres. */
const SCREEN_OUTSET = 0.22;
/** Points along one screen: enough for a smooth curve at the display's sizes. */
const SCREEN_STEPS = 12;

/** One screen in the world: where its foot runs on the ground, how tall it is, and what it says. */
export interface CoverScreen {
  threatId: string;
  level: CueLevel;
  /** True for open: a flat arc on the ground and nothing standing. */
  flat: boolean;
  /** Its foot along the ring, start to end, at the figure's feet. */
  ground: Vec3[];
  /** How tall it stands, in metres (0 when flat). */
  height: number;
  /** The tooltip: "Ork shoota (A): Half, the crate". */
  tip: string;
}

/** The figure the screens stand round. */
interface Bearer {
  x: number;
  z: number;
  elevation: number;
  footprint: number;
}

const LEVEL_WORDS: Record<CueLevel, string> = {
  none: 'Open',
  low: 'Low',
  half: 'Half',
  threeQuarter: '¾',
  full: 'Full',
  out: 'out of sight',
};

/** A cover level in words, as the tooltip says it ("out of sight") or, starting a line, as the readout does. */
export function levelWord(level: CueLevel, { capital = false } = {}): string {
  const word = LEVEL_WORDS[level] ?? LEVEL_WORDS.none;
  return capital ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

/**
 * A figure's screens seen through this camera: built round where he stands, projected, and split about his own
 * depth. The actor's, the ghost's and the hovered figure's all come through here.
 */
export function screensSeen(token: Token, readings: readonly ThreatReading[], camera: Camera): ProjectedScreen[] {
  const at = (p: Vec3) => project(p, camera, SCENE_VIEW);
  return projectScreens(coverScreens(token, readings, tokenHeight(token)), at, at(tokenFoot(token)).depth);
}

/** "Ork shoota (A): Half, the crate" - what a screen's tooltip says. */
export function screenTip(reading: Pick<ThreatReading, 'name' | 'level' | 'source'>): string {
  return `${reading.name}: ${LEVEL_WORDS[reading.level]}${reading.source ? `, the ${reading.source}` : ''}`;
}

/** How tall a screen stands for this level, for a figure this tall: the share of him the cover hides. */
export function screenHeight(level: CueLevel, figureHeight: number): number {
  if (level === 'none') return 0;
  return (level === 'out' ? 1 : COVER_FRACTIONS[level]) * figureHeight;
}

/** One screen per reading, on the ring round the bearer. */
export function coverScreens(bearer: Bearer, readings: readonly ThreatReading[], figureHeight: number): CoverScreen[] {
  // The base ring is drawn at the footprint, a radius (figures.ts `drawnRadius`); the screens stand outside it.
  const radius = bearer.footprint + SCREEN_OUTSET;
  return readings.map((r) => {
    const ground = Array.from({ length: SCREEN_STEPS + 1 }, (_, i) => {
      const deg = r.bearing - SCREEN_SPAN_DEG / 2 + (SCREEN_SPAN_DEG * i) / SCREEN_STEPS;
      const a = (deg * Math.PI) / 180;
      return { x: bearer.x + radius * Math.cos(a), y: bearer.elevation, z: bearer.z + radius * Math.sin(a) };
    });
    const height = screenHeight(r.level, figureHeight);
    return { threatId: r.threatId, level: r.level, flat: r.level === 'none', ground, height, tip: screenTip(r) };
  });
}

/** A projected point: screen x, y and its depth (larger is further, `project()`'s convention). */
export interface DepthPoint {
  x: number;
  y: number;
  depth: number;
}

/** A run of one screen on one side of the figure. */
export interface ScreenRun {
  /** Behind the figure: drawn before his body. */
  far: boolean;
  foot: DepthPoint[];
  top: DepthPoint[];
  /** This run holds the screen's first / last end, where an edge post stands. */
  startsScreen: boolean;
  endsScreen: boolean;
}

const lerp = (a: DepthPoint, b: DepthPoint, t: number): DepthPoint => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
  depth: a.depth + (b.depth - a.depth) * t,
});

/**
 * Split a screen into the runs behind and in front of the figure, cutting exactly where its foot crosses his
 * own depth. `foot` and `top` are the same points projected at the ground and at the screen's height.
 */
export function splitByDepth(foot: DepthPoint[], top: DepthPoint[], centreDepth: number): ScreenRun[] {
  const runs: ScreenRun[] = [];
  let cur: ScreenRun | null = null;
  for (let i = 0; i < foot.length; i++) {
    const far = foot[i].depth > centreDepth;
    if (cur && cur.far !== far) {
      // Close the run at the crossing and open the next one there, so the two meet without a gap.
      const t = (centreDepth - foot[i - 1].depth) / (foot[i].depth - foot[i - 1].depth);
      const cutFoot = lerp(foot[i - 1], foot[i], t);
      const cutTop = lerp(top[i - 1], top[i], t);
      cur.foot.push(cutFoot);
      cur.top.push(cutTop);
      cur = { far, foot: [cutFoot], top: [cutTop], startsScreen: false, endsScreen: false };
      runs.push(cur);
    } else if (!cur) {
      cur = { far, foot: [], top: [], startsScreen: true, endsScreen: false };
      runs.push(cur);
    }
    cur.foot.push(foot[i]);
    cur.top.push(top[i]);
  }
  if (cur) cur.endsScreen = true;
  return runs;
}

/** A screen on the screen: its runs either side of the figure (or its flat arc), ready to draw and to hit-test. */
export interface ProjectedScreen {
  threatId: string;
  level: CueLevel;
  flat: boolean;
  tip: string;
  /** Open's hot arc on the ground; empty for a standing screen. */
  arc: DepthPoint[];
  /** A standing screen's runs, foot and full-height top; empty for open. */
  runs: ScreenRun[];
}

/** Project screens with the map's projection, splitting each about the figure's own depth. */
export function projectScreens(
  screens: readonly CoverScreen[],
  project: (p: Vec3) => DepthPoint,
  centreDepth: number,
): ProjectedScreen[] {
  return screens.map((s) => {
    const base = { threatId: s.threatId, level: s.level, flat: s.flat, tip: s.tip };
    const foot = s.ground.map(project);
    if (s.flat) return { ...base, arc: foot, runs: [] };
    const top = s.ground.map((p) => project({ ...p, y: p.y + s.height }));
    return { ...base, arc: [], runs: splitByDepth(foot, top, centreDepth) };
  });
}

/** Where a run's top stands at `rise` (0..1) of its height: the screen growing out of the ground. */
export function risenTop(run: ScreenRun, rise: number): DepthPoint[] {
  if (rise >= 1) return run.top;
  return run.top.map((t, i) => lerp(run.foot[i], t, rise));
}

/** Is the point inside this polygon (even-odd)? */
function insidePolygon(p: { x: number; y: number }, poly: readonly DepthPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i];
    const b = poly[j];
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** How near a flat arc the pointer must come to count as on it, in scene units. */
const ARC_HIT = 6;

function nearPolyline(p: { x: number; y: number }, line: readonly DepthPoint[]): boolean {
  return line.some((q) => Math.hypot(q.x - p.x, q.y - p.y) <= ARC_HIT);
}

const meanDepth = (pts: readonly DepthPoint[]) => pts.reduce((sum, q) => sum + q.depth, 0) / (pts.length || 1);

/** How far the part of this screen under the pointer stands (larger is further), or null when it is not under it. */
function hitDepth(p: { x: number; y: number }, s: ProjectedScreen): number | null {
  if (s.flat) return nearPolyline(p, s.arc) ? meanDepth(s.arc) : null;
  const hit = s.runs.filter((r) => insidePolygon(p, [...r.foot, ...[...r.top].reverse()]));
  return hit.length ? Math.min(...hit.map((r) => meanDepth(r.foot))) : null;
}

/**
 * The tooltip under the pointer, or null. Screens never take the pointer - anything inside a marker that does
 * would steal its drag, its tape and its clicks - so the tip is found by geometry instead (decision 6). Where two
 * overlap it names the one drawn in front, the nearer, whatever order the guns came in.
 */
export function screenTipAt(p: { x: number; y: number }, screens: readonly ProjectedScreen[]): string | null {
  let best: { tip: string; depth: number } | null = null;
  for (const s of screens) {
    const depth = hitDepth(p, s);
    if (depth !== null && (!best || depth < best.depth)) best = { tip: s.tip, depth };
  }
  return best?.tip ?? null;
}
